import { Check, ChevronDown, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { MATERIAL_FAVOURITES, type BlockMaterial } from "@mcp/assets";
import type { PlanKind } from "@mcp/plan";
import { controller } from "../app/controller.ts";
import { useApp } from "../state/store.ts";
import { blockLabel } from "./schematics/format.ts";
import "./pickers.css";

type Kind = Exclude<PlanKind, "marker">;

function Tile({ m, on, onPick }: { m: BlockMaterial; on: boolean; onPick: (m: BlockMaterial) => void }) {
  return (
    <button type="button" role="option" aria-selected={on} className={`mat-tile${on ? " on" : ""}`} title={`${blockLabel(m.id)}\n${m.id}`} aria-label={blockLabel(m.id)} onClick={() => onPick(m)}>
      <img src={controller.materialThumb(m)} alt="" width={36} height={36} draggable={false} />
      <span className="mat-ring" aria-hidden="true" />
    </button>
  );
}

/**
 * Block the object is built from: shows it as a texture on the map and names it in the block counts.
 * A popover: search over every block of the loaded jars · used in this world (shared) · suggested for this kind.
 */
export function MaterialPicker({ value, kind, onChange }: { value?: string; kind: Kind; onChange: (id: string | undefined) => void }) {
  const recent = useApp((s) => s.recentMaterials);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrap = useRef<HTMLDivElement>(null);
  const cur = value ? controller.material(value) : null;

  useEffect(() => {
    if (!open) return;
    const off = (e: PointerEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener("pointerdown", off);
    return () => window.removeEventListener("pointerdown", off);
  }, [open]);

  const all = useMemo(() => (open ? controller.materials() : []), [open]);
  const byId = useMemo(() => new Map(all.map((m) => [m.id, m])), [all]);
  const list = (ids: string[]) => ids.map((id) => byId.get(id)).filter((m): m is BlockMaterial => !!m);
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    const words = s.split(/\s+/);
    return all.filter((m) => words.every((w) => m.name.toLowerCase().includes(w) || m.id.includes(w))).slice(0, 96);
  }, [q, all]);

  const pick = (m: BlockMaterial) => {
    onChange(m.id);
    controller.store?.rememberMaterial(m.id);
    setOpen(false);
    setQ("");
  };
  const grid = (ms: BlockMaterial[]) => <div className="mat-grid" role="listbox" aria-label="Blocks">{ms.map((m) => <Tile key={m.id} m={m} on={m.id === value} onPick={pick} />)}</div>;
  const what = kind === "building" ? "walls" : kind === "path" ? "paths" : "areas";

  return (
    <div className="mat-picker" ref={wrap} onKeyDown={(e) => { if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); } }}>
      <button type="button" className={`mat-current${open ? " open" : ""}`} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(!open)} title="Choose the block it's built from">
        {cur ? <img className="mat-cur-img" src={controller.materialThumb(cur)} alt="" width={32} height={32} /> : <span className="mat-none" aria-hidden="true" />}
        <span className="mat-cur-text">
          <span className="mat-cur-name">{value ? blockLabel(value) : "Plain colour"}</span>
          <span className="mat-cur-sub">{value ? <span className="mono">{value.replace(/^minecraft:/, "")}</span> : `Choose a block for its ${what}`}</span>
        </span>
        <ChevronDown className="mat-chevron" size={16} aria-hidden="true" />
      </button>
      {open && (
        <div className="mat-pop" role="dialog" aria-label="Choose a block">
          <div className="field-box mat-search">
            <Search size={16} aria-hidden="true" />
            <input autoFocus placeholder="Search blocks" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search blocks" />
          </div>
          {value && !q && (
            <button type="button" className="mat-plain" onClick={() => { onChange(undefined); setOpen(false); }} title="Show the plain colour again">
              <span className="mat-none" aria-hidden="true" /><span className="grow">Plain colour</span><span className="caption">no block</span>
            </button>
          )}
          {!all.length ? (
            <p className="caption mat-empty">Block materials need your game textures · this world shows map colours only.</p>
          ) : q ? (
            <>
              {grid(results)}
              {!results.length && <p className="caption mat-empty">No block matches “{q.trim()}”</p>}
            </>
          ) : (
            <>
              {recent.length > 0 && <><div className="overline">Used in this world</div>{grid(list(recent))}</>}
              <div className="overline">Suggested for {what}</div>
              {grid(list(MATERIAL_FAVOURITES[kind]))}
              <p className="caption mat-foot">{all.length.toLocaleString("en-US")} blocks available, modded ones from your jars too · type to search</p>
            </>
          )}
          {cur && !q && <p className="caption mat-chosen"><Check size={12} aria-hidden="true" />{blockLabel(cur.id)}</p>}
        </div>
      )}
    </div>
  );
}
